/**
 * Rainbow Track — storefront tracker (P1)
 *
 * Cookieless analytics + retargeting hooks. Sends batched hits to
 * Control Plane /api/track/hit.
 *
 *  - page_view on load
 *  - page_end on visibility hidden / unload (with duration + max scroll %)
 *  - event from window.trackEvent({event, ecommerce})
 *
 * Session id: UUID v4 in sessionStorage (no consent required, sessionStorage
 * is per-tab and exempt from cookie consent under Garante 2021).
 *
 * Persistent visitor id (for retargeting): only stored in localStorage AFTER
 * marketing consent is granted. Tier: identified.
 *
 * Bot UAs are ignored server-side — we still post hits but they get dropped.
 */

(function () {
  if (window.__rbTrack) return;
  window.__rbTrack = true;

  // ─── Config ───────────────────────────────────────────────────────
  // Endpoint paths use single letters (/api/r/c, /api/r/i) so adblock
  // filter lists that target /track/* don't match. The base URL points
  // to the Vercel default until we wire a custom domain (control.*).
  var ENDPOINT_BASE =
    (window.__RB_TRACK_BASE__) ||
    "https://rainbow-control-plane.vercel.app";
  var ENDPOINT_HIT = ENDPOINT_BASE + "/api/r/c";
  var ENDPOINT_WHO = ENDPOINT_BASE + "/api/r/i";

  var BATCH_SIZE = 20;
  var FLUSH_INTERVAL_MS = 5000;
  var SESSION_KEY = "rb_session";
  var PERSIST_KEY = "rb_visitor";
  var SESSION_TTL_MS = 30 * 60 * 1000;

  var DEBUG = /[\?&]rb_track_debug=1/.test(location.search);
  function log() { if (DEBUG && console && console.log) console.log.apply(console, ["[rb-track]"].concat([].slice.call(arguments))); }

  // ─── Session id (sessionStorage, no consent) ──────────────────────
  function uuid4() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (s && s.id && Date.now() - s.lastSeen < SESSION_TTL_MS) {
          s.lastSeen = Date.now();
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
          return s.id;
        }
      }
    } catch (e) { /* sessionStorage unavailable */ }
    var id = uuid4();
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id: id, startedAt: Date.now(), lastSeen: Date.now() }));
    } catch (e) { /* private mode */ }
    return id;
  }

  // ─── Persistent visitor (localStorage, only if consent_marketing) ──
  function getConsent() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        var c = window.Shopify.customerPrivacy.currentVisitorConsent && window.Shopify.customerPrivacy.currentVisitorConsent();
        if (c) {
          return {
            analytics: c.analytics === "yes",
            marketing: c.marketing === "yes",
          };
        }
      }
    } catch (e) {}
    return { analytics: false, marketing: false };
  }

  function getPersistentId() {
    var consent = getConsent();
    if (!consent.marketing) return null;
    try {
      var existing = localStorage.getItem(PERSIST_KEY);
      if (existing && /^[0-9a-f-]{36}$/.test(existing)) return existing;
      var id = uuid4();
      localStorage.setItem(PERSIST_KEY, id);
      return id;
    } catch (e) { return null; }
  }

  // ─── Page context ─────────────────────────────────────────────────
  function getUtm() {
    var p = new URLSearchParams(location.search);
    return {
      source: p.get("utm_source"),
      medium: p.get("utm_medium"),
      campaign: p.get("utm_campaign"),
      content: p.get("utm_content"),
      term: p.get("utm_term"),
    };
  }

  function detectDevice() {
    var ua = navigator.userAgent;
    if (/iPad|Tablet/i.test(ua)) return "tablet";
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
    return "desktop";
  }

  function extractProduct() {
    var m = location.pathname.match(/\/products\/([^\/\?#]+)/);
    if (!m) return { handle: null, price: null };
    var handle = m[1];
    // Try to read price from JSON-LD on PDP
    var price = null;
    try {
      var nodes = document.querySelectorAll('script[type="application/ld+json"]');
      for (var i = 0; i < nodes.length; i++) {
        var data = JSON.parse(nodes[i].textContent);
        if (data && data["@type"] === "Product" && data.offers) {
          var offers = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          var p = parseFloat(offers.price || offers.lowPrice || 0);
          if (!isNaN(p) && p > 0) { price = p; break; }
        }
      }
    } catch (e) {}
    return { handle: handle, price: price };
  }

  // ─── Batching queue ───────────────────────────────────────────────
  var queue = [];
  var flushTimer = null;

  function getCustomerIdentity() {
    // Set by Liquid in theme.liquid when customer is logged into Shopify account.
    // Optional: { id, email, tags? }
    try {
      if (window.__rbCustomer && window.__rbCustomer.id) {
        return {
          customer_id: String(window.__rbCustomer.id),
          customer_email: window.__rbCustomer.email || null,
        };
      }
    } catch (e) {}
    return { customer_id: null, customer_email: null };
  }

  function buildBatch() {
    var consent = getConsent();
    var ident = getCustomerIdentity();
    return {
      hits: queue,
      persistent_id: getPersistentId(),
      customer_id: ident.customer_id,
      customer_email: ident.customer_email,
      consent: consent,
    };
  }

  function flush(useBeacon) {
    if (queue.length === 0) return;
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    var batch = buildBatch();
    queue = [];
    var body = JSON.stringify(batch);
    log("flush", batch);
    // Content-Type "text/plain" makes this a CORS-simple request: NO preflight.
    // Critical for sendBeacon during cross-origin navigation (e.g. PDP → /checkouts).
    // Server parses body via request.text() + JSON.parse() so Content-Type doesn't matter.
    var blobType = "text/plain";
    var sentViaBeacon = false;
    try {
      if (useBeacon && navigator.sendBeacon) {
        sentViaBeacon = navigator.sendBeacon(ENDPOINT_HIT, new Blob([body], { type: blobType }));
      }
    } catch (e) { /* swallow */ }
    // Fallback: fetch with keepalive if beacon was not requested OR returned false
    if (!sentViaBeacon) {
      try {
        fetch(ENDPOINT_HIT, {
          method: "POST",
          headers: { "Content-Type": blobType },
          body: body,
          keepalive: true,
          credentials: "omit",
        }).catch(function () {});
      } catch (e) { /* swallow */ }
    }
  }

  function pushHit(hit) {
    hit.session_id = getSession();
    hit.ts = new Date().toISOString();
    queue.push(hit);
    log("queue", hit);
    if (queue.length >= BATCH_SIZE) {
      flush();
    } else if (!flushTimer) {
      flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
  }

  // ─── Initial page_view ────────────────────────────────────────────
  var pageStart = Date.now();
  var maxScroll = 0;

  function fireInitialPageView() {
    var product = extractProduct();
    pushHit({
      type: "page_view",
      path: location.pathname + location.search,
      title: document.title,
      referrer: document.referrer || null,
      utm: getUtm(),
      device: detectDevice(),
      language: navigator.language,
      screen: (screen && screen.width && screen.height) ? screen.width + "x" + screen.height : null,
      product_handle: product.handle,
      product_price: product.price,
    });
    // Auto-fire view_item when on a product page. The Liquid snippet
    // tracking-view-item.liquid also fires it via window.trackEvent, but
    // that depends on race-free timing between the inline script and our
    // deferred trackEvent wrapper. Firing here guarantees the hit lands.
    // Server flag did_view_item is a 0/1 boolean so duplicates are no-op.
    if (product.handle && /^\/products\//.test(location.pathname)) {
      pushHit({
        type: "event",
        event_name: "view_item",
        path: location.pathname,
        product_handle: product.handle,
        product_price: product.price,
        currency: (window.getCurrency && window.getCurrency()) || "EUR",
        params: { source: "rb-track-direct" },
      });
    }
  }

  function trackScroll() {
    var docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    if (docHeight <= 0) return;
    var pct = Math.round(((window.scrollY + window.innerHeight) / docHeight) * 100);
    if (pct > 100) pct = 100;
    if (pct > maxScroll) maxScroll = pct;
  }

  function firePageEnd() {
    pushHit({
      type: "page_end",
      path: location.pathname,
      duration_sec: Math.max(0, Math.floor((Date.now() - pageStart) / 1000)),
      scroll_pct: maxScroll,
    });
    flush(true);
  }

  // ─── Hook into theme's existing window.trackEvent ─────────────────
  // Theme's tracking-helpers.liquid defines window.trackEvent({event,ecommerce})
  // We piggyback on it without breaking it.
  // Late-binding: we replace window.trackEvent so any later subscriber that
  // calls window.trackEvent goes through our wrapper.
  var URGENT_EVENTS = { begin_checkout: 1, purchase: 1, lead: 1, newsletter_submit: 1, contact: 1 };
  var originalTrackEvent = window.trackEvent;
  window.trackEvent = function (payload) {
    try {
      if (typeof originalTrackEvent === "function") originalTrackEvent(payload);
    } catch (e) {}
    if (!payload || !payload.event) return;

    var ecommerce = payload.ecommerce || {};
    var firstItem = (Array.isArray(ecommerce.items) && ecommerce.items[0]) || {};
    var eventName = String(payload.event).slice(0, 64);
    pushHit({
      type: "event",
      event_name: eventName,
      path: location.pathname,
      product_handle: firstItem.item_id || firstItem.id || null,
      product_price: typeof firstItem.price === "number" ? firstItem.price : null,
      cart_value: typeof ecommerce.value === "number" ? ecommerce.value : null,
      currency: ecommerce.currency || null,
      params: payload,
    });

    // Urgent events → flush immediately because the page is about to navigate
    // (begin_checkout) or change radically (purchase/lead). Don't wait for the
    // 5s timer or pagehide because some browsers throttle/cancel sendBeacon
    // when the navigation is cross-origin.
    if (URGENT_EVENTS[eventName]) {
      log("urgent flush for", eventName);
      flush(true);
    }
  };

  // ─── Personalization fetch (P1: just stash the response, P4 will use it) ──
  function fetchIdentity() {
    var consent = getConsent();
    var pid = consent.marketing ? getPersistentId() : null;
    var url = ENDPOINT_WHO + (pid ? "?vid=" + encodeURIComponent(pid) : "");
    fetch(url, { method: "GET", credentials: "omit" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        window.__rbVisitor = data;
        log("who-am-i", data);
        // CRITICAL: push cart attributes UPSTREAM as soon as the visitor id
        // is known. Doing it only on checkout-click is a race: keepalive=true
        // doesn't guarantee the request lands BEFORE Shopify snapshots the
        // cart for the checkout page. By writing the attribute now, it's
        // already on the cart when the user later clicks checkout.
        pushCartAttributes();
        // Dispatch event so storefront sections (banners) can react
        try {
          document.dispatchEvent(new CustomEvent("rb:visitor:loaded", { detail: data }));
        } catch (e) {}
      })
      .catch(function () {});
  }

  // ─── Direct checkout-CTA listener ─────────────────────────────────
  // Fire begin_checkout DIRECTLY without depending on tracking-begin-checkout.liquid
  // or the cart cache. Capture phase + sync sendBeacon so the event arrives
  // even when the page navigates cross-origin to /checkouts/* immediately.
  var CHECKOUT_SELECTOR = '[name="checkout"], .checkout-button, [href*="/checkouts"], a[href^="/checkout"], .rb-cart-drawer__checkout';
  var beginCheckoutFiredForSession = false;
  // Inject Rainbow Track + GA4 ids into Shopify cart note_attributes so the
  // orders/create webhook can attribute the purchase server-side.
  // Priority for the visitor identifier (first non-null wins):
  //   1. persistent_id from localStorage  (only if marketing consent granted)
  //   2. server-assigned visitor_id from fetchIdentity() (anonymous tier OK,
  //      hashed daily by IP+UA, no PII, no consent needed)
  //   3. session_id from sessionStorage (always available, essential bucket)
  // The webhook handler resolves whichever id arrives by checking visitor_id
  // first, then falling back to session_id lookup.
  // De-dupe per session+id: avoid spamming /cart/update.js
  var lastPushedAttrs = null;
  function pushCartAttributes() {
    try {
      var rbId = getPersistentId()
        || (window.__rbVisitor && window.__rbVisitor.visitor_id)
        || getSession();
      var gaMatch = (document.cookie || "").match(/_ga=GA1\.\d\.([^;]+)/);
      var gaId = gaMatch ? gaMatch[1] : null;
      var attrs = {};
      if (rbId) attrs._rb_visitor_id = rbId;
      if (gaId) attrs._ga_client_id = gaId;
      if (Object.keys(attrs).length === 0) return;
      var sig = (attrs._rb_visitor_id || "") + "|" + (attrs._ga_client_id || "");
      if (sig === lastPushedAttrs) return; // already in cart
      lastPushedAttrs = sig;
      fetch("/cart/update.js", {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: attrs }),
      }).catch(function () {});
      log("cart attributes pushed", attrs);
    } catch (e) { /* swallow */ }
  }

  function applyBusyState(btn) {
    // Disable button visually to stop users re-tapping while Shopify's
    // /checkouts/ redirect (1-3s) runs. Capture-phase click already fired,
    // so browser-native navigation/submit is in flight and unaffected.
    if (btn.dataset.rbBusy) return;
    btn.dataset.rbBusy = "1";
    btn.style.opacity = "0.65";
    btn.style.pointerEvents = "none";
    btn.setAttribute("aria-busy", "true");
    var spin = document.createElement("span");
    spin.className = "rb-checkout-spinner";
    spin.setAttribute("aria-hidden", "true");
    spin.style.cssText = "display:inline-block;width:14px;height:14px;margin-left:8px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:rb-spin 0.7s linear infinite;vertical-align:middle";
    btn.appendChild(spin);
    if (!document.getElementById("rb-checkout-busy-css")) {
      var s = document.createElement("style");
      s.id = "rb-checkout-busy-css";
      s.textContent = "@keyframes rb-spin{to{transform:rotate(360deg)}}";
      document.head.appendChild(s);
    }
  }

  function onCheckoutClick(e) {
    var btn = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
    if (!btn) return;

    applyBusyState(btn);

    // Always push cart attributes — even if begin_checkout already fired.
    // The user may click checkout multiple times; the cart attrs must always
    // be up-to-date when they finalize.
    pushCartAttributes();

    if (beginCheckoutFiredForSession) return; // de-dupe begin_checkout within session
    beginCheckoutFiredForSession = true;

    var cart = (window.__rainbowCartItems && window.__rainbowCartItems.length > 0)
      ? window.__rainbowCartItems
      : null;
    var firstItem = cart && cart[0] ? cart[0] : null;
    var cartValue = cart
      ? cart.reduce(function (sum, i) { return sum + (i.price / 100) * i.quantity; }, 0)
      : null;

    pushHit({
      type: "event",
      event_name: "begin_checkout",
      path: location.pathname,
      product_handle: firstItem ? (firstItem.handle || firstItem.product_id || null) : null,
      product_price: firstItem ? (firstItem.price / 100) : null,
      cart_value: cartValue,
      currency: (window.getCurrency && window.getCurrency()) || "EUR",
      params: { source: "rb-track-direct" },
    });
    log("urgent flush for begin_checkout (direct listener)");
    flush(true);
  }

  // ─── Section-level tracking (data-rb-section / data-rb-cta) ──────────
  // Fires once per session per section_name when the element is ≥40% visible,
  // and on every click on [data-rb-cta] inside a tagged section. Used to
  // measure home slide engagement (rb-set-spotlight, hero, etc).
  var seenSections = {};
  function fireSectionView(name, slideId) {
    var key = name + "|" + (slideId || "");
    if (seenSections[key]) return;
    seenSections[key] = true;
    pushHit({
      type: "event",
      event_name: "section_view",
      path: location.pathname,
      params: { section_name: name, slide_id: slideId || null, source: "rb-track-section" },
    });
  }
  function onSectionCtaClick(e) {
    var el = e.target.closest && e.target.closest("[data-rb-cta]");
    if (!el) return;
    var sec = el.closest("[data-rb-section]");
    var section_name = sec ? sec.getAttribute("data-rb-section") : null;
    var slide_id = sec ? sec.getAttribute("data-rb-slide-id") : null;
    var cta_type = el.getAttribute("data-rb-cta") || "unknown";
    var cta_label = (el.getAttribute("aria-label") || el.textContent || "").trim().slice(0, 80);
    var href = el.getAttribute("href") || null;
    pushHit({
      type: "event",
      event_name: "section_cta_click",
      path: location.pathname,
      params: {
        section_name: section_name,
        slide_id: slide_id,
        cta_type: cta_type,
        cta_label: cta_label,
        href: href,
        source: "rb-track-section",
      },
    });
  }
  function setupSectionTracking() {
    if (!("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.intersectionRatio >= 0.4) {
          var name = entry.target.getAttribute("data-rb-section");
          var slideId = entry.target.getAttribute("data-rb-slide-id");
          if (name) fireSectionView(name, slideId);
        }
      });
    }, { threshold: [0.4] });
    var nodes = document.querySelectorAll("[data-rb-section]");
    for (var i = 0; i < nodes.length; i++) io.observe(nodes[i]);
  }

  // ─── Wiring ───────────────────────────────────────────────────────
  function init() {
    fireInitialPageView();
    fetchIdentity();
    window.addEventListener("scroll", trackScroll, { passive: true });
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") flush(true);
    });
    document.addEventListener("click", onCheckoutClick, true);
    document.addEventListener("click", onSectionCtaClick, true);
    setupSectionTracking();
    window.addEventListener("pagehide", firePageEnd);
    // pagehide is more reliable than beforeunload on mobile Safari
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
